import {
  Component,
  EventEmitter,
  Output,
  Input,
  ChangeDetectionStrategy, HostBinding,
} from '@angular/core';
import {
  trigger,
  animate,
  transition,
  state,
  style,
} from '@angular/animations';
import { UserService, User } from '../../../core/services/user.service';
import { AmplitudeService } from '../../../core/services/amplitude.service';
import { AnalyticsEvent } from '../../../core/analytics/analytics-events';
import { ReleaseNotesService } from '../../services/release-notes.service';
import { LayoutState } from '../../../app.component';
import { environment } from '../../../../environments/environment';
import {BoxComponent} from '@shared/components/box/box.component';
import { NgClass } from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {Router, RouterLink, RouterLinkActive} from '@angular/router';
import {MatDividerModule} from '@angular/material/divider';
import {MatListModule} from '@angular/material/list';
import {MatMenuModule} from '@angular/material/menu';
import { PowerBiReportTypeEnum } from '@shared/models/reports.model';
import { provideTranslocoScope, TranslocoPipe } from '@jsverse/transloco';

// This helper component should only be used by app-sidebar.
@Component({
    selector: 'nav-section',
    template: `
    <ng-container>
      <mat-list-item (click)="expanded = !expanded" class="no-wrap space-between">
        <mat-icon matListItemIcon [svgIcon]="svgIcon" />
        <div style="white-space: normal; line-height: normal">
          {{ sectionName }}
        </div>
        <mat-icon svgIcon="chevron-down-white" class="nav__chevron-down"
                  [@chevron]="expanded ? 'expanded' : 'collapsed'"
                  style="color: #fafafa; flex: none;"
         />
      </mat-list-item>

      <div>
        <mat-nav-list
          style="overflow: hidden"
          [@section]="expanded ? 'expanded' : 'collapsed'"
        >
          <ng-content></ng-content>
        </mat-nav-list>
      </div>
    </ng-container>
  `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    animations: [
        trigger('section', [
            state('expanded', style({ height: '*', visibility: 'visible' })),
            state('collapsed', style({ height: '0px' })),
            transition('expanded => collapsed', animate('200ms ease-in')),
            transition('collapsed => expanded', animate('200ms ease-in')),
        ]),
        trigger('chevron', [
            state('expanded', style({ transform: 'rotate(-180deg)' })),
            state('collapsed', style({ transform: 'rotate(0)' })),
            transition('expanded => collapsed', animate('200ms ease-in')),
            transition('collapsed => expanded', animate('200ms ease-out')),
        ]),
    ],
    imports: [
        MatListModule,
        MatIconModule
    ]
})
export class NavSectionComponent {
  @Input() public svgIcon = '';
  @Input() public sectionName = '';
  public expanded = false;
}

const navItems = [
  {
    route: 'home',
    name: 'sharedNav.appSidebar.overview',
    icon: 'overview',
    disabled: false,
  },
  {
    route: 'deals',
    name: 'sharedNav.appSidebar.deals',
    icon: 'deals',
    disabled: false,
  },
  {
    route: 'contacts',
    name: 'sharedNav.appSidebar.contacts',
    icon: 'contacts',
    disabled: false,
  },
  {
    route: 'companies',
    name: 'sharedNav.appSidebar.companies',
    icon: 'building',
    disabled: false,
  },
];

@Component({
    selector: 'app-sidebar',
    templateUrl: './app-sidebar.component.html',
    styleUrls: ['./app-sidebar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [provideTranslocoScope('sharedNav')],
    imports: [
    BoxComponent,
    NgClass,
    MatButtonModule,
    MatIconModule,
    RouterLink,
    MatDividerModule,
    MatListModule,
    RouterLinkActive,
    NavSectionComponent,
    MatMenuModule,
    TranslocoPipe
]
})
export class AppSidebarComponent {
  @Input() public layoutState!: LayoutState;
  @Output() public onNavToggle = new EventEmitter();
  @Output() public onMobileNavToggle = new EventEmitter();

  @HostBinding('class.icon-view')
  get iconView() {
    return this.layoutState.isIconView;
  }
  public navItems = navItems;
  public fullLogo = `assets/images/svg/Newmark-logo.svg`;
  public iconLogo = `assets/images/svg/Newmark-logo-icon.svg`;
  public user!: User;
  public releaseNoteUrl!: string;
  public readonly monolithBaseUrl = environment.monolithBaseUrl;
  public enableResources = false;
  public enableQuotesReport = false;

  public constructor(userService: UserService, releaseNotesService: ReleaseNotesService,
    private router: Router,
    private amplitude: AmplitudeService
  ) {
    userService.get().subscribe((user) => {
      this.user = user;
      this.enableQuotesReport = this.user.reports?.findIndex(x => x.key === PowerBiReportTypeEnum.Quotes) > 0;
    });

    releaseNotesService.releaseNotes$
      .pipe().subscribe((notification: { url: string }) => {
        const cachedNotification = localStorage.getItem('releaseNotification');
        if(notification){
          this.releaseNoteUrl = notification.url ?? (cachedNotification ? JSON.parse(cachedNotification).url : '');
        }
        else {
          this.releaseNoteUrl = (cachedNotification ? JSON.parse(cachedNotification).url : '');
        }

      });
  }

  public toggleNavigation(): void {
    this.onNavToggle.emit();
  }

  public toggleMobileNavigation(): void {
    this.onMobileNavToggle.emit();
  }

  public onBrokerReportsClick() {
    this.amplitude.track(AnalyticsEvent.BrokerReportsOpened, {
      source: 'sidebar',
    });
  }

  public showFreshWidget() {
    this.amplitude.track(AnalyticsEvent.SupportOpened, {
      source: 'sidebar',
    });
    const user = this.user;
    const FreshworksWidget = (window as any).FreshworksWidget;
    FreshworksWidget('identify', 'ticketForm', {
      name: user.name,
      email: user.email,
      product_id: 3000005902 // ideal 2.0
    }, {
      formId: 3000000227
    });
    FreshworksWidget('open', 'ticketForm', {
      formId: 3000000227
    });
  }

  public openQuotesIntelligence() {
    this.router.navigate(['quotes-intelliegence-dashboard']);
  }
}
